import {
  createStore,
  del as deleteValue,
  entries as getEntries,
  get as getValue,
  set as setValue,
} from 'idb-keyval';
import { Capacitor } from '@capacitor/core';
import { SecureStorage, type SecureStoragePlugin } from '@aparajita/capacitor-secure-storage';

export type OfflineStorageScope = {
  userId: string;
  venueId: string;
};

export type OfflineStorageRecordType =
  | 'attachment'
  | 'draft-counter'
  | 'id-mapping'
  /**
   * An in-progress capture that is **not yet complete enough for the write log** (Trackd, 2026-08-07).
   *
   * The write log refuses a payload the API would reject, so until a capture carries every mandatory
   * field there is nowhere durable for it to live — which is why cancelling a half-finished incident
   * used to lose it. Cleared by `clearScope`, so it follows the same user/venue-swap rules as every
   * other record type; nothing extra to remember on logout.
   */
  | 'partial-capture'
  | 'reference-data'
  | 'session'
  | 'write-log';

export type OfflineStorageEntry<T> = {
  key: string;
  value: T;
};

export interface OfflineStorage {
  get<T>(
    scope: OfflineStorageScope,
    recordType: OfflineStorageRecordType,
    key: string,
  ): Promise<T | undefined>;
  put<T>(
    scope: OfflineStorageScope,
    recordType: OfflineStorageRecordType,
    key: string,
    value: T,
  ): Promise<void>;
  list<T>(
    scope: OfflineStorageScope,
    recordType: OfflineStorageRecordType,
  ): Promise<Array<OfflineStorageEntry<T>>>;
  delete(
    scope: OfflineStorageScope,
    recordType: OfflineStorageRecordType,
    key: string,
  ): Promise<void>;
  clearScope(scope: OfflineStorageScope): Promise<void>;
}

export type BrowserOfflineStorageDependencies = {
  del: (key: string) => Promise<void>;
  entries: () => Promise<Array<[string, unknown]>>;
  get: (key: string) => Promise<unknown>;
  set: (key: string, value: unknown) => Promise<void>;
};

type StoredRecord = {
  key: string;
  recordType: OfflineStorageRecordType;
  scope: OfflineStorageScope;
  value: unknown;
};

const createStorageKey = (
  scope: OfflineStorageScope,
  recordType: OfflineStorageRecordType,
  key: string,
) => `${scope.userId}:${scope.venueId}:${recordType}:${key}`;

const NATIVE_STORAGE_KEY_PREFIX = 'idu-offline:';

const createNativeStorageKey = (
  scope: OfflineStorageScope,
  recordType: OfflineStorageRecordType,
  key: string,
) => `${NATIVE_STORAGE_KEY_PREFIX}${createStorageKey(scope, recordType, key)}`;

const isInScope = (record: StoredRecord, scope: OfflineStorageScope) =>
  record.scope.userId === scope.userId && record.scope.venueId === scope.venueId;

const browserStore = createStore('idu-offline-storage', 'records');

const defaultBrowserDependencies: BrowserOfflineStorageDependencies = {
  del: (key) => deleteValue(key, browserStore),
  entries: async () =>
    (await getEntries(browserStore)).flatMap(([key, value]) =>
      typeof key === 'string' ? [[key, value] as [string, unknown]] : [],
    ),
  get: (key) => getValue(key, browserStore),
  set: (key, value) => setValue(key, value, browserStore),
};

export type NativeSecureOfflineStorageDependencies = Pick<
  SecureStoragePlugin,
  'get' | 'keys' | 'remove' | 'set'
>;

const readNativeRecord = async <T>(
  dependencies: NativeSecureOfflineStorageDependencies,
  key: string,
) => {
  const value = await dependencies.get(key);
  return value === null ? undefined : (value as T);
};

export const createNativeSecureOfflineStorage = (
  dependencies: NativeSecureOfflineStorageDependencies = SecureStorage,
): OfflineStorage => ({
  async get<T>(scope: OfflineStorageScope, recordType: OfflineStorageRecordType, key: string) {
    const record = await readNativeRecord<StoredRecord>(
      dependencies,
      createNativeStorageKey(scope, recordType, key),
    );
    return record && isInScope(record, scope) && record.recordType === recordType
      ? (record.value as T)
      : undefined;
  },
  async put(scope, recordType, key, value) {
    await dependencies.set(createNativeStorageKey(scope, recordType, key), {
      key,
      recordType,
      scope,
      value,
    });
  },
  async list<T>(scope: OfflineStorageScope, recordType: OfflineStorageRecordType) {
    const keys = await dependencies.keys();
    const records = await Promise.all(
      keys
        .filter((key) => key.startsWith(NATIVE_STORAGE_KEY_PREFIX))
        .map((key) => readNativeRecord<StoredRecord>(dependencies, key)),
    );

    return records.flatMap((record) =>
      record && record.recordType === recordType && isInScope(record, scope)
        ? [{ key: record.key, value: record.value as T }]
        : [],
    );
  },
  async delete(scope, recordType, key) {
    await dependencies.remove(createNativeStorageKey(scope, recordType, key));
  },
  async clearScope(scope) {
    const keys = await dependencies.keys();
    await Promise.all(
      keys
        .filter((key) => key.startsWith(NATIVE_STORAGE_KEY_PREFIX))
        .map(async (key) => ({
          key,
          record: await readNativeRecord<StoredRecord>(dependencies, key),
        }))
        .map(async (candidate) => {
          const { key, record } = await candidate;
          if (record && isInScope(record, scope)) await dependencies.remove(key);
        }),
    );
  },
});

// `Capacitor.isPluginAvailable()` is NOT a reliable signal here: this plugin's JS
// module unconditionally registers `ios`/`android` implementations (see its
// `registerPlugin('SecureStorage', { ios: ..., android: ... })` call), so
// `isPluginAvailable` reports true on every native platform regardless of whether the
// app actually links the native SecureStorage code. Apps that don't bundle it (e.g.
// Checkd, which has no offline-first requirement) would then invoke a
// native method with no corresponding native implementation — the plugin proxy calls
// back into itself with no plugin header to terminate on, recursing until the JS
// thread stack-overflows and the app is unusable.
//
// The only signal that reflects what's actually *linked* into the native binary is
// `Capacitor.PluginHeaders`, which the native runtime injects into `window.Capacitor`
// at bridge init, containing one entry per plugin that has a compiled native
// implementation. Only route through secure storage when that header is present;
// otherwise fall back to the browser adapter, same as web.
const hasNativeSecureStorage = () => {
  const pluginHeaders = (Capacitor as unknown as { PluginHeaders?: Array<{ name: string }> })
    .PluginHeaders;
  return (
    Array.isArray(pluginHeaders) &&
    pluginHeaders.some((pluginHeader) => pluginHeader.name === 'SecureStorage')
  );
};

export const createPlatformOfflineStorage = (): OfflineStorage =>
  Capacitor.isNativePlatform() && hasNativeSecureStorage()
    ? createNativeSecureOfflineStorage()
    : createBrowserOfflineStorage();

export const createBrowserOfflineStorage = (
  dependencies: BrowserOfflineStorageDependencies = defaultBrowserDependencies,
  onError?: (error: unknown) => void,
): OfflineStorage => {
  const run = async <T>(operation: () => Promise<T>): Promise<T> => {
    try {
      return await operation();
    } catch (error) {
      onError?.(error);
      throw error;
    }
  };

  return {
    async get<T>(scope: OfflineStorageScope, recordType: OfflineStorageRecordType, key: string) {
      const record = await run(() => dependencies.get(createStorageKey(scope, recordType, key)));
      return (record as StoredRecord | undefined)?.value as T | undefined;
    },
    async put(
      scope: OfflineStorageScope,
      recordType: OfflineStorageRecordType,
      key: string,
      value: unknown,
    ) {
      await run(() =>
        dependencies.set(createStorageKey(scope, recordType, key), {
          key,
          recordType,
          scope,
          value,
        } satisfies StoredRecord),
      );
    },
    async list<T>(scope: OfflineStorageScope, recordType: OfflineStorageRecordType) {
      const entries = await run(() => dependencies.entries());
      return entries.flatMap(([, value]) => {
        const record = value as StoredRecord;
        return record.recordType === recordType && isInScope(record, scope)
          ? [{ key: record.key, value: record.value as T }]
          : [];
      });
    },
    async delete(scope: OfflineStorageScope, recordType: OfflineStorageRecordType, key: string) {
      await run(() => dependencies.del(createStorageKey(scope, recordType, key)));
    },
    async clearScope(scope: OfflineStorageScope) {
      const entries = await run(() => dependencies.entries());
      await Promise.all(
        entries.flatMap(([key, value]) =>
          isInScope(value as StoredRecord, scope) ? [run(() => dependencies.del(key))] : [],
        ),
      );
    },
  };
};

export const createInMemoryOfflineStorage = (): OfflineStorage => {
  const records = new Map<string, StoredRecord>();

  return {
    async get<T>(scope: OfflineStorageScope, recordType: OfflineStorageRecordType, key: string) {
      return records.get(createStorageKey(scope, recordType, key))?.value as T | undefined;
    },
    async put<T>(
      scope: OfflineStorageScope,
      recordType: OfflineStorageRecordType,
      key: string,
      value: T,
    ) {
      records.set(createStorageKey(scope, recordType, key), {
        key,
        recordType,
        scope,
        value,
      });
    },
    async list<T>(scope: OfflineStorageScope, recordType: OfflineStorageRecordType) {
      return Array.from(records.entries())
        .filter(([, record]) => record.recordType === recordType && isInScope(record, scope))
        .map(([, record]) => ({
          key: record.key,
          value: record.value as T,
        }));
    },
    async delete(scope: OfflineStorageScope, recordType: OfflineStorageRecordType, key: string) {
      records.delete(createStorageKey(scope, recordType, key));
    },
    async clearScope(scope: OfflineStorageScope) {
      for (const [key, record] of records) {
        if (isInScope(record, scope)) records.delete(key);
      }
    },
  };
};
